import React, { useEffect, useState, useCallback } from 'react'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { T, useContent } from '../../content/ContentProvider'

const REFRESH_MS = 10000

function fmtNum(n) {
  if (n === null || n === undefined) return '—'
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'k'
  return n.toLocaleString('en-US')
}

function Kpi({ label, value, unit, sub, accent }) {
  return (
    <div className={'kpi ' + (accent ? 'kpi--accent' : '')}>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">
        {value}
        {unit && <span style={{ fontSize: 14, color: 'var(--text-muted)', marginLeft: 4 }}>{unit}</span>}
      </div>
      {sub && <div className="kpi__sub">{sub}</div>}
    </div>
  )
}

export function Analytics() {
  const { t } = useContent()
  const [system, setSystem] = useState(null)
  const [summary, setSummary] = useState(null)
  const [users, setUsers] = useState(null)
  const [companies, setCompanies] = useState(null)
  const [logs, setLogs] = useState(null)
  const [logLines, setLogLines] = useState(200)
  const [logFilter, setLogFilter] = useState('')
  const [logsLoading, setLogsLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastRefresh, setLastRefresh] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const [sys, sum, usr, comp] = await Promise.all([
        api.get('/api/admin/analytics/system').then((r) => r.data),
        api.get('/api/admin/analytics/summary').then((r) => r.data),
        api.get('/api/admin/analytics/users').then((r) => r.data),
        api.get('/api/admin/analytics/companies').then((r) => r.data),
      ])
      setSystem(sys)
      setSummary(sum)
      setUsers(usr)
      setCompanies(comp)
      setError('')
      setLastRefresh(new Date())
    } catch (err) {
      setError(err.response?.data?.description || err.response?.data?.message || t('analytics.error.load'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, REFRESH_MS)
    return () => clearInterval(interval)
  }, [load])

  const loadLogs = useCallback(async (n) => {
    setLogsLoading(true)
    try {
      const r = await api.get(`/api/admin/analytics/logs?lines=${n}`)
      setLogs(r.data)
    } catch (err) {
      setLogs({ error: err.response?.data?.description || t('analytics.logs.error.load'), lines: [] })
    } finally {
      setLogsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadLogs(logLines)
  }, [logLines, loadLogs])

  const downloadLogs = () => {
    const token = localStorage.getItem('auth_token_admin')
    // Fetch with auth header then trigger download
    fetch('/api/admin/analytics/logs/download', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(t('analytics.logs.error.download'))
        return r.blob().then((blob) => ({ blob, filename: r.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] || 'mas.log' }))
      })
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((err) => alert(t('analytics.logs.error.downloadAlert') + err.message))
  }

  const filteredLogLines = logs?.lines
    ? logFilter.trim()
      ? logs.lines.filter((l) => l.toLowerCase().includes(logFilter.toLowerCase()))
      : logs.lines
    : []

  const heapPct = system?.memory
    ? Math.round((system.memory.heapUsedMB / system.memory.heapMaxMB) * 100)
    : 0

  return (
    <Layout crumb={t('analytics.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="analytics.title" /></h1>
              <div className="page-head__sub">
                <T
                  k="analytics.head.sub"
                  vars={{
                    state: lastRefresh
                      ? t('analytics.head.refreshed', { time: lastRefresh.toLocaleTimeString() })
                      : t('analytics.head.loading'),
                  }}
                />
              </div>
            </div>
            <div className="page-head__actions">
              <button className="btn" onClick={load} disabled={loading}>
                {Icon.refresh({ s: 12 })} {loading ? <T k="analytics.action.refreshBusy" /> : <T k="analytics.action.refresh" />}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{ marginBottom: 14 }}
          >
            {error}
          </div>
        )}

        {/* ─── System health KPIs ─────────────────────────────────── */}
        <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <T k="analytics.section.systemHealth" />
        </div>
        <div className="kpi-grid">
          <Kpi
            label={t('analytics.kpi.heap.label')}
            value={fmtNum(system?.memory?.heapUsedMB)}
            unit={t('analytics.kpi.heap.unit')}
            sub={system?.memory ? t('analytics.kpi.heap.sub', { max: fmtNum(system.memory.heapMaxMB), pct: heapPct }) : '—'}
            accent={heapPct > 80}
          />
          <Kpi
            label={t('analytics.kpi.rss.label')}
            value={fmtNum(system?.memory?.rssMB)}
            unit={t('analytics.kpi.rss.unit')}
            sub={t('analytics.kpi.rss.sub')}
          />
          <Kpi
            label={t('analytics.kpi.dbPool.label')}
            value={fmtNum(system?.dbPool?.active ?? 0)}
            sub={t('analytics.kpi.dbPool.sub', { max: system?.dbPool?.max ?? '—', idle: system?.dbPool?.idle ?? 0 })}
          />
          <Kpi
            label={t('analytics.kpi.requests.label')}
            value={fmtNum(system?.http?.total)}
            sub={t('analytics.kpi.requests.sub', { ms: system?.http?.avgLatencyMs ?? '—' })}
          />
          <Kpi
            label={t('analytics.kpi.sessions.label')}
            value={fmtNum(system?.redis?.activeSessions ?? 0)}
            sub={t('analytics.kpi.sessions.sub', { keys: system?.redis?.totalKeys ?? 0 })}
          />
          <Kpi
            label={t('analytics.kpi.uptime.label')}
            value={system?.uptime?.human || '—'}
            sub={t('analytics.kpi.uptime.sub')}
          />
        </div>

        {/* ─── DB summary tiles ──────────────────────────────────── */}
        <div style={{ marginTop: 20, marginBottom: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <T k="analytics.section.businessData" />
        </div>
        <div className="kpi-grid">
          <Kpi label={t('analytics.kpi.companies.label')} value={fmtNum(summary?.companies)} />
          <Kpi label={t('analytics.kpi.users.label')} value={fmtNum(summary?.users)} sub={users ? t('analytics.kpi.users.sub', { admins: users.admins, active: users.active }) : ''} />
          <Kpi label={t('analytics.kpi.partnerships.label')} value={fmtNum(summary?.partnerships)} />
          <Kpi label={t('analytics.kpi.transactions.label')} value={fmtNum(summary?.transactions)} accent />
          <Kpi label={t('analytics.kpi.usageEvents.label')} value={fmtNum(summary?.usageEvents)} />
        </div>

        {/* ─── Users table ──────────────────────────────────────── */}
        <div className="grid-2" style={{ marginTop: 20 }}>
          <div className="card">
            <div className="card__head">
              <div>
                <div className="card__title"><T k="analytics.users.title" /></div>
                <div className="card__sub"><T k="analytics.users.sub" vars={{ total: users?.total ?? 0 }} /></div>
              </div>
            </div>
            <div className="card__body card__body--flush">
              <div className="tbl-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table className="tbl tbl--compact table--cards">
                  <thead>
                    <tr>
                      <th><T k="analytics.users.column.user" /></th>
                      <th><T k="analytics.users.column.role" /></th>
                      <th><T k="analytics.users.column.company" /></th>
                      <th><T k="analytics.users.column.status" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(users?.users || []).map((u) => (
                      <tr key={u.id}>
                        <td className="bold" data-label={t('analytics.users.column.user')}>
                          {u.fullName?.trim() || u.username}
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{u.email}</div>
                        </td>
                        <td data-label={t('analytics.users.column.role')}>
                          <span className={'pill pill--' + (u.role === 'ADMIN' ? 'ok' : 'neutral')}>
                            <span className="pill-dot" />
                            {u.role}
                          </span>
                        </td>
                        <td className="muted" data-label={t('analytics.users.column.company')}>
                          {u.companyName || '—'}
                          {u.companyRics && (
                            <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-dim)' }}>
                              {u.companyRics}
                            </div>
                          )}
                        </td>
                        <td data-label={t('analytics.users.column.status')}>
                          <span className={'pill pill--' + (u.enabled ? 'ok' : 'err')}>
                            <span className="pill-dot" />
                            {u.enabled ? t('analytics.users.status.active') : t('analytics.users.status.disabled')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ─── Companies by transaction volume ─────────────────── */}
          <div className="card">
            <div className="card__head">
              <div>
                <div className="card__title"><T k="analytics.companies.title" /></div>
                <div className="card__sub"><T k="analytics.companies.sub" /></div>
              </div>
            </div>
            <div className="card__body card__body--flush">
              <div className="tbl-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table className="tbl tbl--compact table--cards">
                  <thead>
                    <tr>
                      <th><T k="analytics.companies.column.company" /></th>
                      <th><T k="analytics.companies.column.rics" /></th>
                      <th className="num"><T k="analytics.companies.column.transactions" /></th>
                      <th className="num"><T k="analytics.companies.column.share" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(companies?.companies || []).slice(0, 30).map((c) => (
                      <tr key={c.companyId}>
                        <td className="bold" data-label={t('analytics.companies.column.company')}>
                          {c.name}
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{c.country}</div>
                        </td>
                        <td data-label={t('analytics.companies.column.rics')}>
                          <span className="rics">{c.rics}</span>
                        </td>
                        <td className="num bold" data-label={t('analytics.companies.column.transactions')}>{fmtNum(c.transactions)}</td>
                        <td className="num muted" data-label={t('analytics.companies.column.share')}>{c.share}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Server Logs ──────────────────────────────────────── */}
        <div style={{ marginTop: 20, marginBottom: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <T k="analytics.section.serverLogs" />
        </div>
        <div className="card">
          <div className="card__head">
            <div>
              <div className="card__title"><T k="analytics.logs.title" /></div>
              <div className="card__sub">
                {logs?.totalLines ? t('analytics.logs.sub', { lines: logs.totalLines.toLocaleString(), size: (logs.totalSizeBytes / 1024).toFixed(1) }) : '—'}
                {logs?.path && <span style={{ fontFamily: 'var(--font-mono)', marginLeft: 10 }}>{logs.path}</span>}
              </div>
            </div>
            <div className="card__actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                value={logLines}
                onChange={(e) => setLogLines(parseInt(e.target.value, 10))}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  background: '#ffffff',
                }}
              >
                <option value={100}>{t('analytics.logs.lines100')}</option>
                <option value={200}>{t('analytics.logs.lines200')}</option>
                <option value={500}>{t('analytics.logs.lines500')}</option>
                <option value={1000}>{t('analytics.logs.lines1000')}</option>
                <option value={5000}>{t('analytics.logs.lines5000')}</option>
              </select>
              <input
                type="text"
                placeholder={t('analytics.logs.filterPlaceholder')}
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  background: '#ffffff',
                  width: 180,
                }}
              />
              <button className="btn" onClick={() => loadLogs(logLines)} disabled={logsLoading}>
                {Icon.refresh({ s: 12 })} {logsLoading ? <T k="analytics.logs.refreshBusy" /> : <T k="analytics.logs.refresh" />}
              </button>
              <button className="btn btn--primary" onClick={downloadLogs}>
                {Icon.download({ s: 12 })} <T k="analytics.logs.download" />
              </button>
            </div>
          </div>
          <div className="card__body card__body--flush">
            <pre
              style={{
                background: '#0F1320',
                color: '#E5E7EB',
                padding: '12px 16px',
                margin: 0,
                fontFamily: 'var(--font-mono, Consolas, monospace)',
                fontSize: 11.5,
                lineHeight: 1.5,
                maxHeight: 480,
                overflow: 'auto',
                whiteSpace: 'pre',
                borderRadius: '0 0 var(--r-lg) var(--r-lg)',
              }}
            >
              {logs?.error ? (
                <span style={{ color: '#F87171' }}>{logs.error}</span>
              ) : filteredLogLines.length === 0 ? (
                <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>
                  {logFilter ? t('analytics.logs.empty.noMatch', { filter: logFilter }) : t('analytics.logs.empty.none')}
                </span>
              ) : (
                filteredLogLines.map((line, i) => {
                  const color =
                    line.includes(' ERROR ') ? '#F87171' :
                    line.includes(' WARN ')  ? '#FBBF24' :
                    line.includes(' DEBUG ') ? '#94A3B8' :
                    line.includes(' INFO ')  ? '#E5E7EB' :
                    '#CBD5E1'
                  return (
                    <div key={i} style={{ color }}>
                      {line}
                    </div>
                  )
                })
              )}
            </pre>
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
          <T k="analytics.footer" vars={{ seconds: REFRESH_MS / 1000 }} />
        </div>
        </div>
      </div>
    </Layout>
  )
}
